import React from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import GirlsFlagStoryFlyer from './girls-flag/GirlsFlagStoryFlyer';
import GirlsFlagInterestForm from './girls-flag/GirlsFlagInterestForm';
import SocialShareButtons from './jamboree/SocialShareButtons';
import { registrationPricing } from '../mock';

// Dedicated landing page for the Girls Flag Football program.
// - Hero + program value props (Ages 12-14, $140, non-contact, girls-only)
// - Downloadable 9:16 Instagram Story flyer parents can reshare
// - Express Interest form that posts to /api/contact with a Girls-Flag subject
const HERO_STYLE = {
  padding: 'clamp(3rem, 8vw, 5rem) 1.25rem 2.5rem',
  textAlign: 'center',
  background:
    'radial-gradient(circle at 20% 10%, rgba(255, 215, 0, 0.18) 0%, transparent 45%), ' +
    'linear-gradient(155deg, #050505 0%, #150a10 55%, #050505 100%)',
  color: '#fff',
};

const NEW_PILL_STYLE = {
  display: 'inline-block',
  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 100%)',
  color: '#000',
  padding: '0.35rem 0.9rem',
  borderRadius: '999px',
  fontSize: '0.72rem',
  fontWeight: 900,
  letterSpacing: '2.5px',
  marginBottom: '1.2rem',
  boxShadow: '0 6px 18px rgba(255, 215, 0, 0.4)',
};

const H1_STYLE = {
  fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
  fontWeight: 900,
  color: '#FFD700',
  margin: 0,
  lineHeight: 1,
  letterSpacing: '2px',
  textShadow: '0 6px 40px rgba(255, 215, 0, 0.35)',
  fontStyle: 'italic',
  transform: 'skewX(-4deg)',
};

const SUB_STYLE = {
  color: '#fff',
  fontSize: 'clamp(1rem, 2.3vw, 1.3rem)',
  marginTop: '1rem',
  fontWeight: 600,
  letterSpacing: '2px',
  textTransform: 'uppercase',
};

const HOOK_STYLE = {
  maxWidth: '640px',
  margin: '1.5rem auto 0',
  color: '#e8e8e8',
  fontSize: 'clamp(1rem, 2vw, 1.1rem)',
  lineHeight: 1.55,
};

const STATS_ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: '1rem',
  maxWidth: '760px',
  margin: '2rem auto 0',
  padding: '0 0.5rem',
};

const STAT_STYLE = {
  background: 'rgba(255, 215, 0, 0.08)',
  border: '1px solid rgba(255, 215, 0, 0.35)',
  borderRadius: '14px',
  padding: '1rem',
  textAlign: 'center',
};

const STAT_LABEL_STYLE = {
  color: '#FFD700',
  fontSize: '0.7rem',
  letterSpacing: '2.5px',
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: '0.4rem',
};

const STAT_VALUE_STYLE = {
  color: '#fff',
  fontSize: '1.2rem',
  fontWeight: 800,
};

const SECTION_STYLE = {
  padding: 'clamp(3rem, 6vw, 4.5rem) 1.25rem',
  background: '#0b0b0b',
};

const SECTION_TITLE_STYLE = {
  color: '#FFD700',
  fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
  fontWeight: 900,
  textAlign: 'center',
  marginBottom: '0.5rem',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
};

const SECTION_SUB_STYLE = {
  color: '#c8c8c8',
  fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
  textAlign: 'center',
  maxWidth: '620px',
  margin: '0 auto 2.5rem',
  lineHeight: 1.55,
};

const FORM_WRAPPER_STYLE = {
  maxWidth: '720px',
  margin: '0 auto',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 215, 0, 0.25)',
  borderRadius: '16px',
  padding: 'clamp(1.5rem, 4vw, 2.25rem)',
};

const GirlsFlagPage = () => {
  const price = registrationPricing.girlsFlag?.[0]?.price ?? 140;

  return (
    <div className="page-container">
      <Navigation />

      <main className="main-content">
        {/* HERO */}
        <section style={HERO_STYLE} data-testid="girls-flag-hero">
          <span style={NEW_PILL_STYLE}>✨ NEWEST PROGRAM</span>
          <h1 style={H1_STYLE}>GIRLS FLAG FOOTBALL</h1>
          <div style={SUB_STYLE}>Ages 12 – 14 · Girls Only</div>
          <p style={HOOK_STYLE}>
            Fast-paced, non-contact flag football built specifically for young
            women. Team-first, skill-focused, and empowering the next generation
            of female athletes on and off the field.
          </p>

          <div style={STATS_ROW_STYLE}>
            <div style={STAT_STYLE}>
              <div style={STAT_LABEL_STYLE}>Season Cost</div>
              <div style={STAT_VALUE_STYLE}>${price}</div>
            </div>
            <div style={STAT_STYLE}>
              <div style={STAT_LABEL_STYLE}>Format</div>
              <div style={STAT_VALUE_STYLE}>No Contact</div>
            </div>
            <div style={STAT_STYLE}>
              <div style={STAT_LABEL_STYLE}>Roster</div>
              <div style={STAT_VALUE_STYLE}>Girls Only</div>
            </div>
            <div style={STAT_STYLE}>
              <div style={STAT_LABEL_STYLE}>First Practice</div>
              <div style={STAT_VALUE_STYLE}>Free</div>
            </div>
          </div>
        </section>

        {/* STORY FLYER */}
        <section style={SECTION_STYLE}>
          <h2 style={SECTION_TITLE_STYLE}>Grab the Story Flyer</h2>
          <p style={SECTION_SUB_STYLE}>
            Download the 1080×1920 flyer below and post it to your Instagram or
            Facebook Story. It has a scannable QR that brings friends right
            back to this page.
          </p>
          <GirlsFlagStoryFlyer />
          <SocialShareButtons
            shareTitle="Randallstown Panthers — Girls Flag Football (Ages 12-14)"
            testIdPrefix="girls-flag-share"
          />
        </section>

        {/* INTEREST FORM */}
        <section style={{ ...SECTION_STYLE, background: '#050505' }}>
          <h2 style={SECTION_TITLE_STYLE}>Express Interest</h2>
          <p style={SECTION_SUB_STYLE}>
            Drop your info below and a Panthers coach will follow up within 48
            hours with practice times, registration links, and next steps.
          </p>
          <div style={FORM_WRAPPER_STYLE}>
            <GirlsFlagInterestForm />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GirlsFlagPage;
